'use client'

import { useState, FormEvent } from 'react'
import { UserPlus, LogIn } from 'lucide-react'
import { Language } from '@/lib/brief613/translations'
import { useBrief613Translation } from '@/hooks/useBrief613Translation'
import { panel, headingClass, mutedClass, errorBox, labelClass, fieldClass, primaryBtnInline } from './ui'

interface RegisterFormProps {
  onSuccess: (user: { id: string; username: string; email: string; role: string; companyName?: string | null }) => void
  onSwitchToLogin: () => void
  language: Language
}

export default function RegisterForm({ onSuccess, onSwitchToLogin, language }: RegisterFormProps) {
  const t = useBrief613Translation(language)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    const formData = new FormData(e.currentTarget)
    const data: any = {}

    for (const [key, value] of formData.entries()) {
      data[key] = value.toString().trim()
    }

    // Validate
    if (!data.username || !data.email || !data.password || !data.company_name) {
      setError(t.register.allFieldsRequired)
      setLoading(false)
      return
    }

    if (data.password.length < 6) {
      setError(t.register.passwordTooShort)
      setLoading(false)
      return
    }

    if (data.password !== data.password_confirm) {
      setError(t.register.passwordsDontMatch)
      setLoading(false)
      return
    }

    try {
      const response = await fetch('/api/brief613/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: data.username,
          email: data.email,
          password: data.password,
          password_confirm: data.password_confirm,
          company_name: data.company_name,
        }),
      })

      const result = await response.json()

      if (result.success) {
        onSuccess(result.user)
      } else {
        setError(result.error || t.register.error)
      }
    } catch (err: any) {
      setError(err.message || t.register.error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={`${panel} p-8 max-w-md mx-auto`}>
      <div className="text-center mb-6">
        <UserPlus className="mx-auto mb-4 text-white/50" size={32} />
        <h2 className={headingClass}>{t.register.title}</h2>
        <p className={`${mutedClass} mt-2`}>{t.register.subtitle}</p>
      </div>

      {error && (
        <div className={`mb-6 ${errorBox}`}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className={labelClass}>{t.register.username} *</label>
          <input
            type="text"
            name="username"
            required
            className={fieldClass}
            placeholder={t.register.usernamePlaceholder}
            disabled={loading}
          />
        </div>

        <div>
          <label className={labelClass}>{t.register.email} *</label>
          <input
            type="email"
            name="email"
            required
            className={fieldClass}
            placeholder={t.register.emailPlaceholder}
            disabled={loading}
          />
        </div>

        <div>
          <label className={labelClass}>{t.register.companyName} *</label>
          <input
            type="text"
            name="company_name"
            required
            className={fieldClass}
            placeholder={t.register.companyNamePlaceholder}
            disabled={loading}
          />
        </div>

        <div>
          <label className={labelClass}>{t.register.password} *</label>
          <input
            type="password"
            name="password"
            required
            minLength={6}
            className={fieldClass}
            placeholder={t.register.passwordPlaceholder}
            disabled={loading}
          />
        </div>

        <div>
          <label className={labelClass}>{t.register.confirmPassword} *</label>
          <input
            type="password"
            name="password_confirm"
            required
            minLength={6}
            className={fieldClass}
            placeholder={t.register.confirmPasswordPlaceholder}
            disabled={loading}
          />
        </div>

        <div className="flex justify-between items-center gap-3">
          <button
            type="button"
            onClick={onSwitchToLogin}
            className="text-white/55 hover:text-white/85 text-xs flex items-center gap-2 transition-colors"
            disabled={loading}
          >
            <LogIn size={16} />
            {t.register.haveAccount} {t.register.login}
          </button>
          <button
            type="submit"
            className={primaryBtnInline}
            disabled={loading}
          >
            {loading ? t.register.registering : (
              <>
                <UserPlus size={16} />
                {t.register.registerButton}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}
